import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";
import { getZmanimLabels, fmtTime, useZmanim } from "../components/zmanimData";

/**
 * Section « Horaires halakhiques » (zmanim) — thème 2026.
 *
 * Aperçu compact des zmanim du JOUR (les mêmes que la page
 * `/horaires-halakhiques`, calculés par HebcalService côté front à partir de
 * `org.lat/long` + `org.zmanimShitat`). Le prochain zman à venir est mis en
 * avant, et un lien renvoie vers la page complète.
 *
 * props (éditables en admin) :
 *   - eyebrow / title / subtitle : en-tête de section
 *   - limit : nombre de zmanim affichés (défaut 6 ; 0 = tous)
 *
 * COULEURS : 100% dérivées des tokens du thème → s'adapte à chaque org.
 */

const styles = `
.t26-zm__card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  max-width: 620px;
  margin-inline: auto;
}
.t26-zm__head {
  padding: 13px 20px;
  background:
    radial-gradient(80% 100% at 100% 0%, color-mix(in oklab, var(--secondary) 14%, transparent) 0%, transparent 70%),
    color-mix(in oklab, var(--primary) 8%, var(--surface));
  border-bottom: 1px solid var(--line);
  text-align: center;
  font-size: 13px;
  color: var(--ink-3);
  text-transform: capitalize;
}
.t26-zm__list { list-style: none; margin: 0; padding: 0; }
.t26-zm__row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line);
}
.t26-zm__row:last-child { border-bottom: 0; }
.t26-zm__row--next {
  background: color-mix(in oklab, var(--primary) 7%, var(--surface));
}
.t26-zm__label {
  font-size: 14px; color: var(--ink-2, var(--ink));
  flex: 1; min-width: 0;
}
.t26-zm__row--next .t26-zm__label { color: var(--primary); font-weight: 600; }
.t26-zm__badge {
  font-size: 11px; font-weight: 600;
  padding: 3px 10px; border-radius: 999px;
  background: var(--primary); color: var(--primary-contrast);
  white-space: nowrap;
}
.t26-zm__time {
  font-family: var(--font-display);
  font-size: 16px; font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.t26-zm__row--next .t26-zm__time { color: var(--primary); }

@media (max-width: 560px) {
  .t26-zm__row { padding: 11px 15px; }
  .t26-zm__label { font-size: 13px; }
}
`;

export default function Zmanim({ context, limit = 6, eyebrow, title, subtitle }) {
    const { org, langValue } = context;
    const locale = langValue || "fr";
    const labels = getZmanimLabels();

    const today = React.useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const info = useZmanim(org, today);

    // Pas de position org, ou calcul pas encore monté (SSR) → section masquée.
    if (!info || !Array.isArray(info.zmanim) || info.zmanim.length === 0) return null;

    // On centre l'aperçu autour du PROCHAIN zman plutôt que de couper bêtement
    // au début de la journée : à 18h, afficher l'aube n'a aucun intérêt.
    const max = Number(limit) > 0 ? Number(limit) : info.zmanim.length;
    const nextIdx = info.zmanim.findIndex(z => z.next);
    let start = 0;
    if (nextIdx >= 0 && info.zmanim.length > max) {
        start = Math.min(Math.max(0, nextIdx - 1), info.zmanim.length - max);
    }
    const shown = info.zmanim.slice(start, start + max);

    const headDate = new Intl.DateTimeFormat(locale, {
        weekday: "long", day: "numeric", month: "long",
        timeZone: info.tz || undefined,
    }).format(today);

    const hdateLabel = info.hdate
        ? `${info.hdate.getDate()} ${info.hdate.getMonthName()} ${info.hdate.getFullYear()}`
        : null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className="t26-section">
                <SectionHead
                    eyebrow={eyebrow || t`Zmanim`}
                    title={title || t`Horaires halakhiques`}
                    subtitle={subtitle || t`Les horaires du jour calculés pour ${org.ville || org.name}`}
                />

                <div className="t26-zm__card">
                    <div className="t26-zm__head">
                        {headDate}{hdateLabel ? ` · ${hdateLabel}` : ""}
                    </div>
                    <ul className="t26-zm__list">
                        {shown.map((z, i) => (
                            <li key={i} className={`t26-zm__row${z.next ? " t26-zm__row--next" : ""}`}>
                                <span className="t26-zm__label">{labels[z.nom] || z.nom}</span>
                                {z.next && <span className="t26-zm__badge"><Trans>Prochain</Trans></span>}
                                <span className="t26-zm__time">{fmtTime(z.time, locale, info.tz)}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div style={{ textAlign: "center" }}>
                    <Link href={`${org.BASE_WEB_URL}/horaires-halakhiques`} legacyBehavior>
                        <a className="t26-section__more">
                            <Trans>Tous les horaires halakhiques</Trans>
                            <span className="t26-section__more-arrow">
                                <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                            </span>
                        </a>
                    </Link>
                </div>
            </section>
        </>
    );
}
